'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { getSocketConfig } from '@/actions/socket';
import { useSession } from 'next-auth/react';
import { logger } from '@/lib/client-logger';

interface ClientToServerEvents {
  join: (data: { room: string }) => void;
  leave: (data: { room: string }) => void;
}

interface SocketContextType {
  socket: Socket<any, ClientToServerEvents> | null;
  isConnected: boolean;
  joinRoom: (room: string) => void;
  leaveRoom: (room: string) => void;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
  joinRoom: () => {},
  leaveRoom: () => {},
});

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within SocketProvider');
  }
  return context;
};

/**
 * Failed connection attempts in a row reported as an error. Fewer are logged
 * as warnings: socket.io reconnects by itself, and a reload, a sleeping laptop
 * or a CMS deploy abort the polling request ("xhr poll error").
 */
const CONNECT_FAILURES_BEFORE_ERROR = 10;

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket<
    any,
    ClientToServerEvents
  > | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const { data: session } = useSession();
  // The session object changes on every refetch (e.g. on window focus): only a
  // new token needs a new socket.
  const strapiToken = session?.strapiToken;

  useEffect(() => {
    let socketInstance: Socket<any, ClientToServerEvents> | null = null;
    // Set on cleanup: a config that arrives afterwards must not open a socket
    // nobody would ever disconnect.
    let cancelled = false;
    let connectFailures = 0;

    if (!strapiToken) {
      return;
    }

    const initSocket = async () => {
      try {
        const config = await getSocketConfig();
        if (cancelled) return;

        if (!config.url) {
          logger.error(
            'WebSocket URL is missing; socket connection will not be established',
          );
          return;
        }

        socketInstance = io(config.url, {
          auth: { token: strapiToken },
          withCredentials: false,
          transports: ['polling', 'websocket'],
          autoConnect: true,
        });

        socketInstance.on('connect', () => {
          logger.debug('Socket connected', { socketId: socketInstance?.id });
          connectFailures = 0;
          setIsConnected(true);
        });

        socketInstance.on('disconnect', (reason) => {
          logger.debug('Socket disconnected', { reason });
          setIsConnected(false);
        });

        socketInstance.on('connect_error', (error) => {
          connectFailures += 1;
          if (connectFailures === CONNECT_FAILURES_BEFORE_ERROR) {
            logger.error('Socket cannot connect', error, { connectFailures });
          } else {
            logger.warn('Socket connection error', {
              connectFailures,
              errorMessage: error.message,
            });
          }
          setIsConnected(false);
        });

        socketInstance.on('authenticated', (data: { username: string }) => {
          logger.debug('Socket authenticated', { username: data.username });
        });

        socketInstance.on('error', (data: { message: string }) => {
          logger.error('Socket error', undefined, { message: data.message });
        });

        setSocket(socketInstance);
      } catch (error) {
        logger.error('Failed to initialize socket', error);
      }
    };

    void initSocket();

    return () => {
      cancelled = true;
      if (socketInstance) {
        logger.debug('Disconnecting socket');
        socketInstance.disconnect();
      }
    };
  }, [strapiToken]);

  const joinRoom = (room: string) => {
    if (socket?.connected) {
      socket.emit('join', { room });
      logger.debug('Joining socket room', { room });
    }
  };

  const leaveRoom = (room: string) => {
    if (socket?.connected) {
      socket.emit('leave', { room });
      logger.debug('Leaving socket room', { room });
    }
  };

  return (
    <SocketContext.Provider
      value={{ socket, isConnected, joinRoom, leaveRoom }}
    >
      {children}
    </SocketContext.Provider>
  );
};
